import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SnbDocumentKind } from '@munifocus/contracts-gov';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Icons,
  iconSize,
  palette,
  Stack,
  StatusChip,
  Tooltip,
  Typography,
  useSnackbar,
} from '@munifocus/ui';

/**
 * Upload a Service New Brunswick record and extract it.
 *
 * Used from both entry points — the intake form (before a file exists) and the
 * tombstone editor (after) — because the officer's action is identical either
 * way. The difference is only where the result goes, so the caller owns that.
 *
 * Extraction is synchronous and takes a second or two, so this shows a spinner
 * rather than a job status: the officer is waiting for the values, not
 * dispatching work.
 */

/** What a completed upload read, so the panel can report it back to the officer. */
export interface SnbUploadOutcome {
  /** Values recognised on the document. Zero means it was not that record. */
  fieldsRead: number;
  /** Provider notes, e.g. "No PAN was found on this document." */
  warnings: string[];
  /**
   * The caller's handle on this upload, handed back on discard.
   *
   * Needed because Parcel records are no longer one-per-file: discarding by kind
   * would drop all of them, and the officer who uploaded the wrong second parcel
   * wants to remove that one.
   */
  ref?: string;
}

/**
 * A finished upload the caller has kept across a reload.
 *
 * The panel's receipts are local state, so on its own it comes back from a
 * refresh remembering nothing — the caller could still see its staged records
 * attach on submit, but the rows naming them were gone, and with them the only
 * way to discard one. A caller that persists its uploads hands them back here
 * and they render as the receipts they were.
 */
export interface RestoredUpload extends SnbUploadOutcome {
  kind: SnbDocumentKind;
  fileName: string;
  /** Required, unlike on a fresh outcome: a row with no handle cannot be discarded. */
  ref: string;
}

export interface SnbUploadPanelProps {
  onUpload: (document: File, kind: SnbDocumentKind) => Promise<SnbUploadOutcome | void>;
  /**
   * Drop what one record contributed, so the officer can start it over. Without
   * it the only way out of uploading the wrong PDF is to abandon the form.
   *
   * `ref` identifies which upload, for kinds that can appear more than once.
   */
  onDiscard?: (kind: SnbDocumentKind, ref?: string) => void;
  /**
   * Record kinds already extracted, so each shows as done rather than pending.
   *
   * A kind, not a count — this is for records held server-side on a file that
   * already exists, which this panel did not upload and cannot discard. Uploads
   * it should render in full belong in `restored`.
   */
  done?: SnbDocumentKind[];
  /** Uploads recovered from a caller-side draft. See {@link RestoredUpload}. */
  restored?: RestoredUpload[];
  disabled?: boolean;
}

/** A finished upload, as this panel remembers it. */
interface UploadRecord extends SnbUploadOutcome {
  fileName: string;
  /** Local identity, so a discard removes the right row even without a `ref`. */
  key: string;
}

/** A restored upload as this panel's own bookkeeping sees it. */
function toRecord(upload: RestoredUpload): UploadRecord {
  return {
    // Keyed off the staging id rather than the local counter, so a restored row
    // and one uploaded afterwards can never collide on `key`.
    key: `r${upload.ref}`,
    fileName: upload.fileName,
    fieldsRead: upload.fieldsRead,
    warnings: upload.warnings,
    ref: upload.ref,
  };
}

export function SnbUploadPanel({
  onUpload,
  onDiscard,
  done = [],
  restored = [],
  disabled = false,
}: SnbUploadPanelProps) {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const [busy, setBusy] = useState<SnbDocumentKind | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Kept here rather than pushed onto the caller: both entry points want the
  // same receipt, and neither has a reason to own the file name.
  //
  // One PAN, many Parcels. A PAN describes the whole account and a second one
  // replaces the first; a Parcel describes a single PID, and a straddled
  // property has one record per parcel.
  //
  // Seeded once from `restored`, not synced to it: after mount these rows are
  // this panel's to change, and re-reading the prop would undo a discard.
  const [panResult, setPanResult] = useState<UploadRecord | null>(() => {
    const pan = restored.find((r) => r.kind === 'snb_pan');
    return pan ? toRecord(pan) : null;
  });
  const [parcelResults, setParcelResults] = useState<UploadRecord[]>(() =>
    restored.filter((r) => r.kind === 'snb_parcel').map(toRecord),
  );
  const inputs = useRef<Partial<Record<SnbDocumentKind, HTMLInputElement | null>>>({});
  const nextKey = useRef(0);

  async function handleFiles(kind: SnbDocumentKind, files: FileList | null) {
    if (!files || files.length === 0) return;
    // Sequential, not parallel: each upload is cross-checked against the ones
    // before it, so the order the officer chose them has to be the order applied.
    for (const file of Array.from(files)) {
      await handleFile(kind, file);
    }
  }

  async function handleFile(kind: SnbDocumentKind, file: File | undefined) {
    if (!file) return;
    setBusy(kind);
    setError(null);
    try {
      const outcome = await onUpload(file, kind);
      const fieldsRead = outcome?.fieldsRead ?? 0;
      const record: UploadRecord = {
        key: `u${nextKey.current++}`,
        fileName: file.name,
        fieldsRead,
        warnings: outcome?.warnings ?? [],
        ref: outcome?.ref,
      };
      if (kind === 'snb_pan') setPanResult(record);
      else setParcelResults((prev) => [...prev, record]);
      // The receipt below the button says the same thing, but the officer is
      // usually looking at the fields the upload just filled — several sections
      // down the form — and would never see it. A run that read nothing is not a
      // success: it is a wrong-document warning that happens not to have errored.
      if (fieldsRead === 0) {
        snackbar.warning(t('gov.extraction.upload.readNothingToast', { file: file.name }));
      } else {
        snackbar.success(
          t('gov.extraction.upload.readCountToast', { count: fieldsRead, file: file.name }),
        );
      }
    } catch (err) {
      // The API returns an officer-readable message (wrong file type, no text
      // layer, provider not configured); prefer it to a generic failure.
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('gov.extraction.upload.failed');
      setError(message);
      snackbar.error(message);
    } finally {
      setBusy(null);
      // Clear the input so re-selecting the same file fires a change event.
      const input = inputs.current[kind];
      if (input) input.value = '';
    }
  }

  function discard(kind: SnbDocumentKind, record: UploadRecord) {
    if (kind === 'snb_pan') setPanResult(null);
    else setParcelResults((prev) => prev.filter((r) => r.key !== record.key));
    setError(null);
    onDiscard?.(kind, record.ref);
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t('gov.extraction.upload.hint')}
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        {/* PAN — one per file. A second upload replaces the first. */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <input
            ref={(el) => {
              inputs.current['snb_pan'] = el;
            }}
            type="file"
            accept="application/pdf"
            hidden
            data-testid="snb-upload-snb_pan"
            onChange={(event) => void handleFiles('snb_pan', event.target.files)}
          />
          <Button
            fullWidth
            variant={panResult || done.includes('snb_pan') ? 'outlined' : 'contained'}
            disabled={disabled || busy !== null}
            startIcon={
              busy === 'snb_pan' ? (
                <CircularProgress size={16} />
              ) : (
                <Icons.Upload size={iconSize.md} />
              )
            }
            onClick={() => inputs.current['snb_pan']?.click()}
          >
            {busy === 'snb_pan'
              ? t('gov.extraction.upload.extracting')
              : t('gov.extraction.upload.pan')}
          </Button>
          {panResult ? (
            <UploadReceipt
              result={panResult}
              onDiscard={onDiscard ? () => discard('snb_pan', panResult) : undefined}
            />
          ) : (
            done.includes('snb_pan') && (
              <StatusChip
                status="success"
                label={t('gov.extraction.upload.done')}
                sx={{ mt: 0.75 }}
              />
            )
          )}
        </Box>

        {/* Parcel — one per PID. A straddled property has several. */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <input
            ref={(el) => {
              inputs.current['snb_parcel'] = el;
            }}
            type="file"
            accept="application/pdf"
            hidden
            multiple
            data-testid="snb-upload-snb_parcel"
            onChange={(event) => void handleFiles('snb_parcel', event.target.files)}
          />
          <Button
            fullWidth
            variant={
              parcelResults.length > 0 || done.includes('snb_parcel') ? 'outlined' : 'contained'
            }
            disabled={disabled || busy !== null}
            startIcon={
              busy === 'snb_parcel' ? (
                <CircularProgress size={16} />
              ) : (
                <Icons.Upload size={iconSize.md} />
              )
            }
            onClick={() => inputs.current['snb_parcel']?.click()}
          >
            {busy === 'snb_parcel'
              ? t('gov.extraction.upload.extracting')
              : t(
                  parcelResults.length > 0
                    ? 'gov.extraction.upload.parcelAnother'
                    : 'gov.extraction.upload.parcel',
                )}
          </Button>
          {parcelResults.length > 0
            ? parcelResults.map((result) => (
                <UploadReceipt
                  key={result.key}
                  result={result}
                  onDiscard={onDiscard ? () => discard('snb_parcel', result) : undefined}
                />
              ))
            : done.includes('snb_parcel') && (
                <StatusChip
                  status="success"
                  label={t('gov.extraction.upload.done')}
                  sx={{ mt: 0.75 }}
                />
              )}
        </Box>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
    </Box>
  );
}

/**
 * What the upload actually produced: the file name, how many values came off
 * it, and anything the provider noted.
 *
 * A run that reads nothing still succeeds — the PDF parsed, it just was not an
 * SNB record of that kind. Left unsaid, that is indistinguishable from a
 * successful upload whose values the officer has yet to scroll to, so zero
 * fields is called out as a warning rather than reported as done.
 */
function UploadReceipt({ result, onDiscard }: { result: UploadRecord; onDiscard?: () => void }) {
  const { t } = useTranslation();
  const readNothing = result.fieldsRead === 0;

  return (
    <Box sx={{ mt: 1 }}>
      <Stack direction="row" spacing={0.75} sx={{ alignItems: 'flex-start' }}>
        {readNothing ? (
          <Icons.AlertTriangle
            size={iconSize.sm}
            color={palette.semantic.warning}
            style={{ flexShrink: 0, marginTop: 2 }}
          />
        ) : (
          <Icons.Check
            size={iconSize.sm}
            color={palette.semantic.success}
            style={{ flexShrink: 0, marginTop: 2 }}
          />
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            title={result.fileName}
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: palette.text.primary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {result.fileName}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: palette.text.secondary }}>
            {readNothing
              ? t('gov.extraction.upload.readNothing')
              : t('gov.extraction.upload.readCount', { count: result.fieldsRead })}
          </Typography>
          {result.warnings.map((warning) => (
            <Typography key={warning} sx={{ fontSize: 12.5, color: palette.text.muted }}>
              {warning}
            </Typography>
          ))}
        </Box>
        {onDiscard && (
          <Tooltip title={t('gov.extraction.upload.discard')}>
            <IconButton
              size="small"
              aria-label={t('gov.extraction.upload.discard')}
              onClick={onDiscard}
              sx={{ ml: 'auto', flexShrink: 0 }}
            >
              <Icons.X size={iconSize.sm} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
}
